import { createContext, useContext, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

const API = "/api";

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const login = useCallback(async (sessionId, loginType = "customer") => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${API}/auth/session`,
        { session_id: sessionId, login_type: loginType },
        { withCredentials: true }
      );
      
      setUser(response.data);
      setLoading(false);
      
      // Redirect based on role from server response
      if (response.data.role === "admin") {
        navigate("/dashboard", { state: { user: response.data } });
      } else if (response.data.role === "staff") {
        navigate("/staff-schedule", { state: { user: response.data } });
      } else {
        navigate("/portal", { state: { user: response.data } });
      }
    } catch (error) {
      console.error("Login failed:", error);
      setLoading(false);
      toast.error(error.response?.data?.detail || "Login failed");
      navigate("/");
    }
  }, [navigate]);

  const checkAuth = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      setUser(null);
      navigate("/");
    }
  }, [navigate]);

  const initiateGoogleLogin = useCallback((loginType = "customer") => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Encode loginType in the URL PATH so it survives Safari ITP cross-origin redirects
    // localStorage/sessionStorage/queryParams are unreliable on mobile Safari
    const redirectUrl = `${window.location.origin}/auth-callback/${loginType}`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        logout,
        checkAuth,
        initiateGoogleLogin,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
